import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import type { AeCampaign, AeCampaignFunnel, CreateCampaignInput } from '@/types/assessmentEngine';
import { CampaignSetupForm } from '../CampaignSetupForm';
import { CampaignFunnelSummary } from '../CampaignFunnelSummary';
import { CampaignAdminsPanel } from '../CampaignAdminsPanel';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import {
  AE_BTN_DANGER_SOLID,
  AE_BTN_PRIMARY,
  AE_BTN_SECONDARY,
  AE_BTN_SUCCESS,
  AE_CARD_COMPACT,
  AE_MUTED,
} from '@/components/assessment-engine/layout/aeTheme';
import {
  campaignPeriodGranularity,
  campaignResponseModeLabelKey,
} from '@/components/assessment-engine/campaignResponseMode';

function useHubConfigSave(campaign: AeCampaign, onSave: (patch: Partial<CreateCampaignInput>) => Promise<void>) {
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const isDraft = campaign.status === 'draft';
  const isArchived = campaign.status === 'archived';
  const messagingEditable = !isArchived;

  const save = async (
    payload: Partial<CreateCampaignInput>,
    options?: { silent?: boolean }
  ) => {
    setSaving(true);
    try {
      await onSave(payload);
      if (!options?.silent) {
        toast.success(t('assessmentEngine.hub.configSaved'));
      }
    } catch {
      toast.error(t('assessmentEngine.hub.configFailed'));
      throw new Error('config save failed');
    } finally {
      setSaving(false);
    }
  };

  return { saving, save, readOnly: !isDraft, messagingEditable };
}

interface BasicsTabProps {
  campaign: AeCampaign;
  busy: boolean;
  onTransition: (action: 'open' | 'close' | 'reopen') => void;
  onSave: (patch: Partial<CreateCampaignInput>) => Promise<void>;
}

/** Campaign hub — lifecycle, funnel, basics/branding, admin completion emails, campaign admins. */
export const BasicsTab: React.FC<BasicsTabProps> = ({ campaign, busy, onTransition, onSave }) => {
  const { t } = useLanguage();
  const [funnel, setFunnel] = useState<AeCampaignFunnel | null>(null);
  const config = useHubConfigSave(campaign, onSave);

  useEffect(() => {
    assessmentEngineService
      .getCampaignFunnel(campaign.id)
      .then(setFunnel)
      .catch(() => setFunnel(null));
  }, [campaign.id, campaign.status]);

  return (
    <div className="space-y-3">
      <div className={`${AE_CARD_COMPACT} space-y-3`}>
        <p className={`${AE_MUTED} text-xs`}>
          {t('assessmentEngine.hub.status')}:{' '}
          <strong className="text-[var(--color-surface-text)]">
            {t(`assessmentEngine.status.${campaign.status}`)}
          </strong>
          {' · '}
          {t('assessmentEngine.campaign.template')}: {campaign.template?.name ?? campaign.template_id}
          {(() => {
            const labelKey = campaignResponseModeLabelKey(campaign);
            const label =
              labelKey === 'assessmentEngine.responseMode.perPeriodWithGranularity'
                ? t(labelKey, {
                    period: t(
                      `assessmentEngine.period.${campaignPeriodGranularity(campaign.time_period_config) ?? 'quarter'}`
                    ),
                  })
                : t(labelKey);
            return <> · {label}</>;
          })()}
        </p>
        {funnel?.summary && <CampaignFunnelSummary summary={funnel.summary} />}
        <div className="flex flex-wrap gap-2">
          {campaign.status === 'draft' && (
            <button type="button" disabled={busy} className={AE_BTN_SUCCESS} onClick={() => onTransition('open')}>
              {t('assessmentEngine.hub.open')}
            </button>
          )}
          {campaign.status === 'open' && (
            <button type="button" disabled={busy} className={AE_BTN_DANGER_SOLID} onClick={() => onTransition('close')}>
              {t('assessmentEngine.hub.close')}
            </button>
          )}
          {campaign.status === 'closed' && (
            <button type="button" disabled={busy} className={AE_BTN_PRIMARY} onClick={() => onTransition('reopen')}>
              {t('assessmentEngine.hub.reopen')}
            </button>
          )}
          <Link
            to={`/assessment-engine/campaigns/${campaign.id}/report`}
            className={AE_BTN_SECONDARY}
          >
            {t('assessmentEngine.hub.viewReport')}
          </Link>
        </div>
      </div>

      <div className={AE_CARD_COMPACT}>
        <CampaignSetupForm
          mode="edit"
          campaign={campaign}
          hubSection="basics"
          autoSave
          readOnly={config.readOnly}
          messagingEditable={config.messagingEditable}
          benchmarkEditable
          saving={config.saving}
          submitLabel={t('assessmentEngine.settings.save')}
          onSubmit={async (payload, _pending, options) => config.save(payload, options)}
        />
      </div>

      <CampaignAdminsPanel campaignId={campaign.id} />
    </div>
  );
};

/** @deprecated Use BasicsTab */
export const OverviewTab = BasicsTab;

/** Campaign hub — presentation, email verification, completion, follow-up, and respondent copy. */
export const RespondentExperienceTab: React.FC<{
  campaign: AeCampaign;
  onSave: (patch: Partial<CreateCampaignInput>) => Promise<void>;
}> = ({ campaign, onSave }) => {
  const { t } = useLanguage();
  const config = useHubConfigSave(campaign, onSave);

  return (
    <div className={AE_CARD_COMPACT}>
      <CampaignSetupForm
        mode="edit"
        campaign={campaign}
        hubSection="respondent"
        autoSave
        readOnly={config.readOnly}
        messagingEditable={config.messagingEditable}
        benchmarkEditable
        saving={config.saving}
        submitLabel={t('assessmentEngine.settings.save')}
        onSubmit={async (payload, _pending, options) => config.save(payload, options)}
      />
    </div>
  );
};

/** @deprecated Hub distribution settings live on Invitations tab */
export const SetupTab: React.FC<{
  campaign: AeCampaign;
  publicUrl?: string;
  onSave: (patch: Partial<CreateCampaignInput>) => Promise<void>;
}> = () => null;

/** @deprecated */
export const ConfigurationTab = SetupTab;
